/**
 * Componente de loading visual durante a abertura de arquivo
 * com etapas de progresso detalhadas
 */

import React from 'react';
import { Loader2, FileText, CheckCircle2, AlertCircle, Database, Key, Send, Clock } from 'lucide-react';

type AbrirArquivoStage = 'buscando_info' | 'buscando_smb' | 'enviando_comando' | 'aguardando_corel' | 'concluido' | 'erro';

interface AbrirArquivoLoadingProps {
  arquivoNome: string;
  visible: boolean;
  stage: AbrirArquivoStage;
}

const steps: Array<{ 
  key: AbrirArquivoStage; 
  label: string; 
  icon: React.ReactNode;
  description: string;
}> = [
  { 
    key: 'buscando_info', 
    label: 'Buscando informações', 
    icon: <Database className="w-4 h-4" />,
    description: 'Carregando dados do arquivo...'
  },
  { 
    key: 'buscando_smb', 
    label: 'Obtendo credenciais', 
    icon: <Key className="w-4 h-4" />,
    description: 'Preparando autenticação SMB...'
  },
  { 
    key: 'enviando_comando', 
    label: 'Enviando comando', 
    icon: <Send className="w-4 h-4" />,
    description: 'Preparando requisição...'
  },
  { 
    key: 'aguardando_corel', 
    label: 'Aguardando CorelDRAW', 
    icon: <Clock className="w-4 h-4" />,
    description: 'Abrindo arquivo no CorelDRAW...'
  },
];

export function AbrirArquivoLoading({ arquivoNome, visible, stage }: AbrirArquivoLoadingProps) {
  if (!visible) return null;

  const currentIndex = steps.findIndex(s => s.key === stage);
  const isComplete = stage === 'concluido';
  const hasError = stage === 'erro';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center animate-in fade-in duration-200">
      <div className={`bg-navy-800 rounded-lg p-6 shadow-xl border max-w-md w-full mx-4 transition-all duration-300 ${
        isComplete 
          ? 'border-green-500/50 bg-green-900/20' 
          : hasError
          ? 'border-red-500/50 bg-red-900/20'
          : 'border-navy-700'
      }`}>
        <div className="flex flex-col gap-4">
          {/* Header com ícone e título */}
          <div className="flex items-start gap-3 pb-2 border-b border-navy-700/50">
            <div className="relative flex-shrink-0">
              {isComplete ? (
                <div className="relative">
                  <CheckCircle2 className="w-12 h-12 text-green-400 animate-bounce" />
                  <div className="absolute inset-0 bg-green-400/20 rounded-full animate-ping" />
                </div>
              ) : hasError ? (
                <AlertCircle className="w-12 h-12 text-red-400" />
              ) : (
                <FileText className="w-12 h-12 text-blue-400" />
              )}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h3 className={`text-xl font-bold mb-1 ${
                isComplete ? 'text-green-300' : hasError ? 'text-red-300' : 'text-white'
              }`}>
                {isComplete ? 'Arquivo aberto!' : hasError ? 'Erro ao abrir' : 'Abrindo arquivo...'}
              </h3>
              <p className="text-sm text-gray-400 truncate font-mono" title={arquivoNome}>
                {arquivoNome}
              </p>
            </div>
          </div>

          {/* Steps de progresso */}
          {!isComplete && !hasError && (
            <div className="space-y-2">
              {steps.map((step, index) => {
                const isActive = index === currentIndex;
                const isCompleted = index < currentIndex;
                const isPending = index > currentIndex;

                return (
                  <div
                    key={step.key}
                    className={`flex items-center gap-3 p-2.5 rounded-lg transition-all duration-300 ${
                      isActive 
                        ? 'bg-blue-500/20 border border-blue-500/30 shadow-sm shadow-blue-500/20' 
                        : isCompleted
                        ? 'bg-green-500/10 border border-green-500/20'
                        : 'bg-navy-700/30 border border-navy-600/30 opacity-50'
                    }`}
                  >
                    {/* Indicador de status */}
                    <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                      {isCompleted ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : isActive ? (
                        <div className="text-blue-400 relative w-5 h-5 flex items-center justify-center">
                          {step.icon}
                        </div>
                      ) : (
                        <div className="text-gray-500 opacity-40">
                          {step.icon}
                        </div>
                      )}
                    </div>

                    {/* Texto do step */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${
                        isActive ? 'text-blue-200' : isCompleted ? 'text-green-300' : 'text-gray-500'
                      }`}>
                        {step.label}
                      </p>
                      {isActive && (
                        <p className="text-xs text-blue-300/70 mt-1 font-medium">
                          {step.description}
                        </p>
                      )}
                    </div>

                    {/* Spinner animado no step ativo */}
                    {isActive && (
                      <Loader2 className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Barra de progresso */}
          {!isComplete && !hasError && (
            <div className="w-full bg-navy-700/50 rounded-full h-2 overflow-hidden shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 rounded-full transition-all duration-700 ease-out shadow-sm shadow-blue-500/50"
                style={{ 
                  width: `${Math.max(0, Math.min(100, ((currentIndex + 1) / steps.length) * 100))}%` 
                }}
              />
            </div>
          )}

          {/* Mensagem de sucesso */}
          {isComplete && (
            <div className="flex items-center gap-2 text-sm text-green-300">
              <CheckCircle2 className="w-4 h-4" />
              <span>Arquivo aberto com sucesso no CorelDRAW</span>
            </div>
          )}

          {/* Mensagem de erro */}
          {hasError && (
            <div className="flex items-center gap-2 text-sm text-red-300">
              <AlertCircle className="w-4 h-4" />
              <span>Ocorreu um erro ao abrir o arquivo</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
